<template>
  <div class="hexagonal-board">
    <div class="hex-grid" :style="gridStyle">
      <div 
        v-for="(cell, index) in hexCells" 
        :key="index"
        class="hex-cell"
        :class="{ 'obstacle': isObstacle(cell.x, cell.y), 'center': cell.x === 0 && cell.y === 0 }"
        :style="{
          'background-color': isObstacle(cell.x, cell.y) ? 'rgba(102, 102, 102, 0.5)' : 'transparent',
          'transform': `translate(${cell.posX}px, ${cell.posY}px)`,
          'width': `${hexWidth}px`,
          'height': `${hexHeight}px`
        }"
      >
        <!-- Optional: Add text inside hexagon -->
        <span v-if="showCoordinates" class="hex-coordinates">
          {{cell.x}},{{cell.y}}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HexagonalBoard',
  props: {
    width: {
      type: Number,
      default: 8
    },
    height: {
      type: Number,
      default: 8
    },
    cellColor: {
      type: String,
      default: 'transparent'
    },
    obstacles: {
      type: Array,
      default: () => []
    },
    showCoordinates: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hexSize: 40, // Base unit size
    }
  },
  computed: {
    // Calculate dimensions for perfect hexagons
    hexWidth() {
      return this.hexSize;
    },
    hexHeight() {
      // Height is approximately 0.866 times the width for a regular hexagon
      return this.hexSize * 0.866;
    },
    // Generate positions for all hexagons in a honeycomb pattern from the center
    hexCells() {
      const cells = [];
      const hexWidth = this.hexWidth;
      const hexHeight = this.hexHeight;
      
      // Determine radius based on the width/height props
      const radius = Math.floor(Math.min(this.width, this.height) / 2);
      
      // Add center cell first
      cells.push({
        x: 0,
        y: 0,
        posX: 0,
        posY: 0
      });
      
      // Create hexagons in rings around the center
      for (let r = 1; r <= radius; r++) {
        // For each ring, we need to generate hexagons in 6 directions
        // Start at the top-right of the ring and go clockwise
        
        // Top-right hexagon
        let x = 0;
        let y = -r;
        let z = r; // We use cube coordinates where x + y + z = 0
        
        // For each of the 6 sides of the ring
        for (let side = 0; side < 6; side++) {
          // For each hex position along this side (except corners which would be counted twice)
          for (let pos = 0; pos < r; pos++) {
            // Use axial coordinates (omit z since it can be calculated from x + y + z = 0)
            const axialX = x;
            const axialY = y;
            
            // Convert to pixel position
            const posX = hexWidth * 3/2 * axialX;
            const posY = hexHeight * (Math.sqrt(3)/2 * axialX + Math.sqrt(3) * axialY);
            
            cells.push({
              x: axialX,
              y: axialY,
              posX: posX,
              posY: posY
            });
            
            // Move to next position along this side
            if (side === 0) { x++; y--; z = -x-y; } // Move right and up
            else if (side === 1) { x++; z--; y = -x-z; } // Move right
            else if (side === 2) { z++; y--; x = -z-y; } // Move down and right
            else if (side === 3) { z++; x--; y = -x-z; } // Move down and left
            else if (side === 4) { x--; y++; z = -x-y; } // Move left
            else if (side === 5) { y++; z--; x = -z-y; } // Move up and left
          }
        }
      }
      
      // Fill in the rest of the hexes within the ring but not on the perimeter
      for (let dx = -radius+1; dx < radius; dx++) {
        for (let dy = -radius+1; dy < radius; dy++) {
          const dz = -dx-dy;
          if (Math.abs(dx) + Math.abs(dy) + Math.abs(dz) <= 2*radius && 
              !cells.some(cell => cell.x === dx && cell.y === dy)) {
            // Convert to pixel position
            const posX = hexWidth * 3/2 * dx;
            const posY = hexHeight * (Math.sqrt(3)/2 * dx + Math.sqrt(3) * dy);
            
            cells.push({
              x: dx,
              y: dy,
              posX: posX,
              posY: posY
            });
          }
        }
      }
      
      return cells;
    },
    // Calculate grid dimensions based on cell positions
    gridDimensions() {
      if (this.hexCells.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
      
      return this.hexCells.reduce((dims, cell) => {
        return {
          minX: Math.min(dims.minX, cell.posX),
          maxX: Math.max(dims.maxX, cell.posX),
          minY: Math.min(dims.minY, cell.posY),
          maxY: Math.max(dims.maxY, cell.posY)
        };
      }, { 
        minX: Infinity, 
        maxX: -Infinity, 
        minY: Infinity, 
        maxY: -Infinity 
      });
    },
    // Compute grid style including dimensions and centering
    gridStyle() {
      const dims = this.gridDimensions;
      const width = dims.maxX - dims.minX + this.hexWidth;
      const height = dims.maxY - dims.minY + this.hexHeight;
      
      // Calculate translation to center the grid
      const translateX = -dims.minX + (this.hexWidth / 2);
      const translateY = -dims.minY + (this.hexHeight / 2);
      
      return {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${translateX}px, ${translateY}px)`,
        // Scale to fit container if needed
        zoom: Math.min(1, Math.min(600 / width, 500 / height))
      };
    }
  },
  methods: {
    isObstacle(x, y) {
      // Check if the cell is an obstacle
      return this.obstacles.some(obstacle => 
        obstacle.x === x && obstacle.y === y
      );
    }
  }
}
</script>

<style scoped>
.hexagonal-board {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 500px;
  position: relative;
  overflow: hidden;
}

.hex-grid {
  position: relative;
  margin: 0 auto;
}

.hex-cell {
  position: absolute;
  /* This clip-path creates a perfect hexagon */
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  /* For browsers that don't support clip-path */
  -webkit-clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  display: flex;
  justify-content: center;
  align-items: center;
  /* Add a thin border to show the hexagon outline */
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.3);
  transition: background-color 0.3s ease;
}

.hex-cell.obstacle {
  background-color: rgba(102, 102, 102, 0.5) !important;
  opacity: 0.7;
}

.hex-cell.center {
  border: 2px solid rgba(0, 0, 0, 0.5);
  /* Optional: Add a subtle indicator for the center hex */
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.2);
}

.hex-coordinates {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.7);
}

/* Additional responsive styling */
@media (max-width: 768px) {
  .hexagonal-board {
    min-height: 400px;
  }
}
</style>